import React, { useEffect, useMemo, useState } from "react";
import DealerNav from "./DealerNav";

export default function DealerProfile() {
  const API = process.env.REACT_APP_API_BASE;
  const token = useMemo(() => localStorage.getItem("dealerToken"), []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    email: "",
  });

  const [meta, setMeta] = useState({
    verified: false,
    verificationStatus: "Unverified",
  });

  const fetchProfile = async () => {
    setLoading(true);
    setErr("");
    setOk("");
    try {
      const res = await fetch(`${API}/dealer/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setForm({
        name: data.name ?? "",
        address: data.address ?? "",
        city: data.city ?? "",
        state: data.state ?? "",
        zip: data.zip ?? "",
        phone: data.phone ?? "",
        email: data.email ?? "",
      });
      setMeta({
        verified: !!data.verified,
        verificationStatus: data.verificationStatus || "Unverified",
      });
    } catch {
      setErr("Could not load profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Name is required.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Email looks invalid.";
    if (form.state && form.state.length > 50) return "State is too long.";
    if (form.zip && form.zip.length > 20) return "Zip is too long.";
    if (form.phone && form.phone.replace(/\D/g, "").length > 50)
      return "Phone is too long.";
    return "";
  };

  const onSave = async () => {
    setErr("");
    setOk("");
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/dealer/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: form.name,
          address: form.address,
          city: form.city,
          state: form.state,
          zip: form.zip,
          phone: form.phone,
          email: form.email,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setOk("Profile saved.");
    } catch {
      setErr("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => fetchProfile();

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <div className="p-4 text-gray-600">Loading…</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4">
      
            <DealerNav />
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Dealer Profile</h1>
        <span
          className={`text-xs px-2 py-1 rounded ${
            meta.verified ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
          }`}
          title={meta.verificationStatus}
        >
          {meta.verified ? "Verified" : meta.verificationStatus || "Unverified"}
        </span>
      </div>

      {/* Alerts */}
      {err && (
        <div className="mb-3 rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
          {err}
        </div>
      )}
      {ok && (
        <div className="mb-3 rounded border border-green-200 bg-green-50 text-green-700 px-3 py-2 text-sm">
          {ok}
        </div>
      )}

      {/* Form */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-600 mb-1">Name *</label>
          <input
            name="name"
            value={form.name}
            onChange={onChange}
            className="w-full border rounded px-3 py-2 text-sm"
            maxLength={255}
            required
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Phone</label>
          <input
            name="phone"
            value={form.phone}
            onChange={onChange}
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder="e.g., (555) 123-4567"
            maxLength={50}
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Public Email</label>
          <input
            name="email"
            value={form.email}
            onChange={onChange}
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder="sales@yourdealership.com"
            maxLength={255}
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-600 mb-1">Address</label>
          <input
            name="address"
            value={form.address}
            onChange={onChange}
            className="w-full border rounded px-3 py-2 text-sm"
            maxLength={100}
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">City</label>
          <input
            name="city"
            value={form.city}
            onChange={onChange}
            className="w-full border rounded px-3 py-2 text-sm"
            maxLength={100}
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">State</label>
          <input
            name="state"
            value={form.state}
            onChange={onChange}
            className="w-full border rounded px-3 py-2 text-sm"
            maxLength={50}
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Zip</label>
          <input
            name="zip"
            value={form.zip}
            onChange={onChange}
            className="w-full border rounded px-3 py-2 text-sm"
            maxLength={20}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex gap-2">
        <button
          onClick={onSave}
          disabled={saving || !form.name.trim()}
          className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
        <button
          onClick={onReset}
          className="px-4 py-2 text-sm rounded border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          Reset
        </button>
      </div>
    </div>
  );
}
